/**
 * Draggable Mixin
 *
 * Provides utility methods for drag and drop functionality.
 * Supports both mouse and touch events.
 *
 * Usage:
 *   import draggable from '@/components/OliverBotV2/mixins/draggable.js'
 *
 *   export default {
 *     mixins: [draggable],
 *     // Use the utility methods provided
 *   }
 *
 * Methods provided:
 *   - getEventCoordinates(event) - Extract clientX/clientY from mouse or touch event
 *   - clamp(value, min, max) - Clamp a value between min and max
 *   - hasDragThresholdExceeded(startX, startY, currentX, currentY, threshold) - Check if drag threshold exceeded
 */
export default {
  methods: {
    /**
     * Get event coordinates for both mouse and touch events
     * @param {Event} event - Mouse or touch event
     * @returns {Object} - {clientX, clientY}
     */
    getEventCoordinates(event) {
      return {
        clientX: event.clientX || (event.touches && event.touches[0]?.clientX),
        clientY: event.clientY || (event.touches && event.touches[0]?.clientY),
      };
    },

    /**
     * Check if drag threshold has been exceeded
     * @param {number} startX - Start X position
     * @param {number} startY - Start Y position
     * @param {number} currentX - Current X position
     * @param {number} currentY - Current Y position
     * @param {number} threshold - Drag threshold (default: 5)
     * @returns {boolean} - True if threshold exceeded
     */
    hasDragThresholdExceeded(
      startX,
      startY,
      currentX,
      currentY,
      threshold = 5
    ) {
      const deltaX = Math.abs(currentX - startX);
      const deltaY = Math.abs(currentY - startY);
      return deltaX > threshold || deltaY > threshold;
    },

    /**
     * Clamp a value between min and max
     * @param {number} value - Value to clamp
     * @param {number} min - Minimum value
     * @param {number} max - Maximum value
     * @returns {number} - Clamped value
     */
    clamp(value, min, max) {
      return Math.max(min, Math.min(max, value));
    },
  },
};
